/**
 * GMA-1625 (CompassCare Web 10/14) — INTEL route container.
 * Issue #358 — live IPAWS watches wiring.
 *
 * Mounts the app-shell (WebShell) around the caregiver-intelligence page
 * (IntelPage) at /web/intel, wiring sidebar navigation to the router, and
 * feeds the ACTIVE WATCHES rail from the live cortex-be endpoint
 * GET /api/v2/ipaws/watches (GMA-1692) through the standard api layer.
 *
 * Data strategy:
 * - Live IPAWS watches (mapped via ipawsWatchMapper) always render when the
 *   fetch resolves.
 * - Under REACT_APP_DEMO_DATA=1 the demo catalog still enriches the rail —
 *   it covers the non-IPAWS watch types (NORA companion, HealthKit trends,
 *   utility, …) whose backends don't exist yet.
 * - Unauthenticated visitors fire NO request (useFetch's #355 session gate);
 *   a 401/network error fails to the calm empty state via useFetch's error
 *   state + the mapper's fail-safe empty output. Never a crash.
 */
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import WebShell from "../../components/web/WebShell";
import IntelPage from "./IntelPage";
import { useFetch } from "../../api/baseApi";
import FeedStatusNote from "../../components/web/FeedStatusNote";
import { summarizeFeedStatus } from "./feedStatusModel";
import {
  mapIpawsWatchesResponse,
  mapRouteAccessWatchesResponse,
  setTelemetry,
} from "@graymattersalliance/compass-map";
import { captureMessage } from "../../config/sentry";

// Corridor-mapper telemetry sink (idempotent with WebMapRoute's bootstrap).
setTelemetry(captureMessage);
import {
  DEMO_DATA_ENABLED,
  getDemoClients,
  getDemoWatches,
  getDemoWatchProfiles,
} from "./demoData";

export default function WebIntelRoute() {
  const navigate = useNavigate();
  // Capture one timestamp so demo-client generation stays deterministic for
  // the render (matches the other /web route containers).
  const now = Date.now();
  // Live IPAWS watches. On 401/network error useFetch keeps data as [] and
  // the mapper fails safe to empty — the page then shows demo data (demo
  // builds) or its calm empty state, never a crash.
  const { data, error: watchesError, loading: watchesLoading } =
    useFetch("ipaws/watches");
  const live = useMemo(() => mapIpawsWatchesResponse(data), [data]);
  // GMA-1737: Traffic & Responder Access cards from the routes lifecycle
  // events (GMA-1736). Same fail-to-empty posture as the IPAWS feed.
  const {
    data: routesData,
    error: routesError,
    loading: routesLoading,
  } = useFetch("routes/access-watches");
  const routesLive = useMemo(
    () => mapRouteAccessWatchesResponse(routesData),
    [routesData],
  );
  const demoEnabled = DEMO_DATA_ENABLED;
  const liveWatches = [...live.watches, ...routesLive.watches];
  const liveClients = [...live.clients, ...routesLive.clients];
  const watches = demoEnabled
    ? [...liveWatches, ...getDemoWatches()]
    : liveWatches;
  // Live impacted-client stubs resolve the live watches' clientIds; demo
  // clients resolve the demo watches'. Ids are prefix-disjoint by design
  // (ipaws- / routes- / numeric demo).
  const clients = demoEnabled
    ? [...getDemoClients(now), ...liveClients]
    : liveClients;
  const profiles = demoEnabled ? getDemoWatchProfiles() : [];
  // #416: a dead feed and a quiet feed both render an empty rail. Name the
  // ones that failed so the operator can tell an outage from a calm shift.
  // Driven by feed state, not list length — under REACT_APP_DEMO_DATA=1 the
  // demo watches fill the rail even when the live feed is down.
  const feedStatus = useMemo(
    () =>
      summarizeFeedStatus([
        { name: "IPAWS alerts", error: watchesError, loading: watchesLoading },
        { name: "Route access", error: routesError, loading: routesLoading },
      ]),
    [watchesError, watchesLoading, routesError, routesLoading],
  );
  return (
    <WebShell
      activeKey="intel"
      onNavigate={(section) => {
        if (section && section.path) navigate(section.path);
      }}
      title="INTEL"
    >
      <FeedStatusNote degraded={feedStatus.degraded} />
      <IntelPage
        watches={watches}
        profiles={profiles}
        clients={clients}
        onGenerateSitrep={(watch) =>
          // GMA-1624: hand the watch to the SITRep board as provenance.
          navigate(
            `/web/intel/sitrep?watch=${encodeURIComponent(watch && watch.id ? watch.id : "")}`,
          )
        }
      />
    </WebShell>
  );
}
